import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faList, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import ToastError from "../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../NotificationToast/ToastSuccess";
import { APILA } from "../../../../../config/apila";
import { APIMS } from "../../../../../config/apims";
import Pagination from "../../../../components/pagination_new";
import "bulma/css/bulma.css";
import "../../../../../index.css";

const API_LIMIT = 1000;
const text = (value) => (value == null ? "" : String(value));

// SD -> SMP -> SMA, lalu urut nama sekolah.
const sortSchools = (items) => {
  const level = (name) => {
    const school = text(name).toUpperCase();
    if (school.includes("SMA")) return 2;
    if (school.includes("SMP")) return 1;
    if (school.includes("SD")) return 0;
    return 3;
  };
  return [...items].sort(
    (a, b) => level(a?.school_name) - level(b?.school_name) ||
      text(a?.school_name).localeCompare(text(b?.school_name), "id", { numeric: true }),
  );
};

export default function PenilaianKaryawan() {
  document.title = "Penilaian Karyawan";

  const token = localStorage.getItem("token");
  const storageBranch = localStorage.getItem("ysb_branch_id");
  const storageLevel = localStorage.getItem("level");
  const location = useLocation();
  const navigate = useNavigate();

  const headers = useMemo(() => ({
    headers: {
      Authorization: `${token}`,
      "Content-Type": "application/json",
    },
  }), [token]);

  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);

  const [semesters, setSemesters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [schools, setSchools] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [filters, setFilters] = useState({
    ysb_semester_id: "",
    ysb_branch_id: "",
    ysb_school_id: "",
  });

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [savingRows, setSavingRows] = useState({});

  const firstLoadDone = useRef(false);
  const schoolRequestRef = useRef(0);
  const tableRequestRef = useRef(0);
  const semesterCache = useRef({});
  const mountedRef = useRef(true);
  const cacheVersionRef = useRef(0);
  const activeSemesterRef = useRef("");
  activeSemesterRef.current = String(filters.ysb_semester_id);
  const pendingSavesRef = useRef(new Set());
  const refreshVersionRef = useRef({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      ++schoolRequestRef.current;
      ++tableRequestRef.current;
    };
  }, []);

  useEffect(() => {
    if (["developer", "superadmin"].includes(storageLevel)) return;
    const roleItems = JSON.parse(localStorage.getItem("role_permission") || "[]");
    const access = roleItems.find((item) => item.menu?.url === location.pathname);
    if (!access) navigate("/dashboard");
  }, [location.pathname, navigate, storageLevel]);

  const yearOptions = useMemo(
    () => [...new Set(semesters.map((item) => item?.name_year).filter(Boolean))],
    [semesters],
  );
  const semesterOptions = useMemo(
    () => semesters
      .filter((item) => item?.name_year === academicYear)
      .sort((a, b) => Number(a?.semester) - Number(b?.semester)),
    [semesters, academicYear],
  );
  const selectedSemester = useMemo(
    () => semesterOptions.find((item) => String(item?.id) === String(filters.ysb_semester_id)),
    [semesterOptions, filters.ysb_semester_id],
  );
  const isFilterComplete = !!selectedSemester?.ysb_academic_year_id &&
    !!filters.ysb_branch_id && !!filters.ysb_school_id && !loadingSchool;

  // Ambil tahun ajaran, semester, dan cabang satu kali saat halaman dibuka.
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [branchRes, semesterRes] = await Promise.all([
          APIMS.get(
            `/api/access/branches?page=1&limit=${API_LIMIT}&ascending=1&search=&branch=${storageBranch}&level=${storageLevel}`,
            headers,
          ),
          APIMS.get(
            `/api/access/semesters?page=1&limit=${API_LIMIT}&ascending=0&search=`,
            headers,
          ),
        ]);
        if (!mountedRef.current) return;

        const branchData = branchRes?.data?.data || [];
        const semesterData = semesterRes?.data?.data || [];
        const active = semesterData.find((item) => Number(item?.status) === 1) || semesterData[0];

        setBranches(branchData);
        setSemesters(semesterData);
        setAcademicYear(active?.name_year || "");
        setFilters((old) => ({
          ...old,
          ysb_semester_id: active?.id || "",
          ysb_branch_id: branchData[0]?.branch_code || "",
        }));
        if (!branchData.length) setLoading(false);
      } catch (error) {
        if (!mountedRef.current) return;
        setLoading(false);
        swal({
          title: "Gagal",
          text: error?.response?.data?.message || "Gagal memuat filter penilaian karyawan.",
          icon: "error",
        });
      }
    };
    loadOptions();
  }, [headers, storageBranch, storageLevel]);

  // Daftar sekolah mengikuti cabang, bukan cabang yang sebelumnya dipilih.
  useEffect(() => {
    if (!filters.ysb_branch_id) return;
    const requestId = ++schoolRequestRef.current;
    const branchId = filters.ysb_branch_id;

    const loadSchools = async () => {
      setLoadingSchool(true);
      try {
        const response = await APIMS.get(
          `/api/access/schools?page=1&limit=${API_LIMIT}&ascending=1&branch=${branchId}&level=${storageLevel}`,
          headers,
        );
        if (!mountedRef.current || requestId !== schoolRequestRef.current) return;
        let schoolData = response?.data?.data || [];
        if (["developer", "superadmin"].includes(storageLevel)) {
          schoolData = schoolData.filter((item) => item?.ysb_branch_id === branchId);
        }
        if (branchId === "BKS") {
          schoolData = schoolData.filter((item) => item?.school_code !== "BASEMENT_BEKASI");
        }
        schoolData = sortSchools(schoolData);
        setSchools(schoolData);
        setFilters((old) => ({
          ...old,
          ysb_school_id: schoolData[0]?.school_code || "",
        }));
      } catch (error) {
        if (!mountedRef.current || requestId !== schoolRequestRef.current) return;
        setSchools([]);
        setFilters((old) => ({ ...old, ysb_school_id: "" }));
      } finally {
        if (mountedRef.current && requestId === schoolRequestRef.current) {
          setLoadingSchool(false);
          setLoading(false);
        }
      }
    };
    loadSchools();
    return () => { ++schoolRequestRef.current; };
  }, [filters.ysb_branch_id, headers, storageLevel]);

  // POST /api/angket-employees/index-new untuk kedua semester secara bersamaan.
  // Cache membuat perpindahan tab tidak perlu memanggil API lagi.
  const fetchData = useCallback(async () => {
    if (!isFilterComplete || !semesterOptions.length) return;
    const requestId = ++tableRequestRef.current;
    ++cacheVersionRef.current;
    setIsFetching(true);
    setSearch("");
    setTablePage(1);

    try {
      const entries = await Promise.all(semesterOptions.map(async (semester) => {
        // ID tahun ajaran HARUS dari data semester, bukan dari teks nama TA.
        const payload = {
          ysb_academic_year_id: semester.ysb_academic_year_id,
          ysb_branch_id: filters.ysb_branch_id,
          ysb_semester_id: semester.id,
          ysb_school_id: filters.ysb_school_id,
        };
        const response = await APILA.post("/api/angket-employees/index-new", payload, headers);
        const data = Array.isArray(response?.data?.data) ? response.data.data : [];

        // Tampilkan hanya data yang cocok dengan sekolah dan semester terpilih.
        const matching = data.filter((item) =>
          String(item?.ysb_academic_year_id) === String(payload.ysb_academic_year_id) &&
          String(item?.ysb_branch_id) === String(payload.ysb_branch_id) &&
          String(item?.ysb_semester_id) === String(payload.ysb_semester_id) &&
          String(item?.ysb_school_id) === String(payload.ysb_school_id)
        );
        return [String(semester.id), matching.map((item, index) => ({
          ...item,
          __rowIndex: index,
          __savedKeluhan: item.ysb_sub_element_value == null
            ? ""
            : String(item.ysb_sub_element_value),
        }))];
      }));

      if (!mountedRef.current || requestId !== tableRequestRef.current) return;
      semesterCache.current = Object.fromEntries(entries);
      setRows(semesterCache.current[String(filters.ysb_semester_id)] || []);
      setHasSearched(true);
      setIsFilterDirty(false);
    } catch (error) {
      if (!mountedRef.current || requestId !== tableRequestRef.current) return;
      semesterCache.current = {};
      setRows([]);
      setHasSearched(false);
      setIsFilterDirty(true);
      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal mengambil data penilaian karyawan.",
        icon: "error",
      });
    } finally {
      if (mountedRef.current && requestId === tableRequestRef.current) setIsFetching(false);
    }
  }, [filters.ysb_branch_id, filters.ysb_school_id, filters.ysb_semester_id, headers, isFilterComplete, semesterOptions]);

  useEffect(() => {
    if (firstLoadDone.current || loading || isFilterDirty || !isFilterComplete) return;
    firstLoadDone.current = true;
    fetchData();
  }, [fetchData, loading, isFilterDirty, isFilterComplete]);

  const invalidateFilter = () => {
    ++tableRequestRef.current;
    ++cacheVersionRef.current;
    semesterCache.current = {};
    setRows([]);
    setSearch("");
    setTablePage(1);
    setIsFetching(false);
    setHasSearched(false);
    setIsFilterDirty(true);
  };

  const changeFilter = (event) => {
    const { name, value } = event.target;
    if (name === "ysb_branch_id") {
      ++schoolRequestRef.current;
      setSchools([]);
      setLoadingSchool(!!value);
    }
    setFilters((old) => ({
      ...old,
      [name]: value,
      ...(name === "ysb_branch_id" ? { ysb_school_id: "" } : {}),
    }));
    invalidateFilter();
  };

  const changeYear = (event) => {
    const year = event.target.value;
    const options = semesters
      .filter((item) => item?.name_year === year)
      .sort((a, b) => Number(a?.semester) - Number(b?.semester));
    const initial = options.find((item) => Number(item?.status) === 1) || options[0];
    setAcademicYear(year);
    setFilters((old) => ({ ...old, ysb_semester_id: initial?.id || "" }));
    invalidateFilter();
  };

  const switchSemester = (semester) => {
    if (!semester?.id || isFetching || String(semester.id) === String(filters.ysb_semester_id)) return;
    activeSemesterRef.current = String(semester.id);
    setFilters((old) => ({ ...old, ysb_semester_id: semester.id }));
    setRows(semesterCache.current[String(semester.id)] || []);
    setSearch("");
    setTablePage(1);
  };

  // Pertahankan nilai per semester dalam cache, agar pindah tab tidak GET ulang.
  const updateCachedRow = (semesterId, rowIndex, updater) => {
    const current = semesterCache.current[semesterId];
    if (!current) return;
    const updated = current.map((item) => item.__rowIndex === rowIndex
      ? updater(item)
      : item);
    semesterCache.current = { ...semesterCache.current, [semesterId]: updated };
    if (activeSemesterRef.current === semesterId) setRows(updated);
  };

  const changeKeluhan = (rowIndex, rawValue) => {
    if (!/^\d*$/.test(rawValue)) return; // Jumlah keluhan hanya bilangan bulat >= 0.
    updateCachedRow(String(filters.ysb_semester_id), rowIndex, (item) => ({
      ...item,
      ysb_sub_element_value: rawValue,
    }));
  };

  // PUT saat input kehilangan fokus (atau saat pengguna menekan Enter).
  // Data/filter diambil dari baris yang bersangkutan, bukan dari tab yang mungkin
  // sudah berubah ketika request selesai.
  const saveKeluhan = async (rowIndex, rawValue) => {
    const semesterId = String(filters.ysb_semester_id);
    const originalRow = (semesterCache.current[semesterId] || [])
      .find((item) => item.__rowIndex === rowIndex);
    if (!originalRow) return;

    const savedValue = originalRow.__savedKeluhan ?? "";
    const value = String(rawValue ?? "").trim();
    if (value === savedValue || (value !== "" && Number(value) === Number(savedValue) && savedValue !== "")) return;

    const rollback = () => updateCachedRow(semesterId, rowIndex, (item) => ({
      ...item,
      ysb_sub_element_value: item.__savedKeluhan,
    }));

    if (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value))) {
      rollback();
      ToastError.fire({ icon: "error", title: "Jumlah Keluhan harus berupa angka bulat 0 atau lebih." });
      return;
    }

   
    const userId = localStorage.getItem("id_admin")?.replace(/[|&;$%@"<>()+,]/g, "");
    const payload = {
      ysb_academic_year_id: originalRow.ysb_academic_year_id || selectedSemester?.ysb_academic_year_id,
      ysb_semester_id: originalRow.ysb_semester_id || semesterId,
      ysb_branch_id: originalRow.ysb_branch_id || filters.ysb_branch_id,
      ysb_school_id: originalRow.ysb_school_id || filters.ysb_school_id,
      ysb_config_anomaly_id: originalRow.ysb_config_anomaly_id,
      ysb_sub_element_value: Number(value),
      user_id: userId,
    };
    if (!payload.ysb_academic_year_id || !payload.ysb_semester_id ||
        !payload.ysb_branch_id || !payload.ysb_school_id ||
        !payload.ysb_config_anomaly_id || !userId) {
      rollback();
      ToastError.fire({ icon: "error", title: "Data penyimpanan belum lengkap. Silakan periksa login dan filter." });
      return;
    }

    const savingKey = `${semesterId}:${payload.ysb_school_id}:${payload.ysb_config_anomaly_id}`;
    if (pendingSavesRef.current.has(savingKey)) return;
    pendingSavesRef.current.add(savingKey);
    setSavingRows((old) => ({ ...old, [savingKey]: true }));
    const cacheVersion = cacheVersionRef.current;

    try {
      const response = await APILA.put("/api/angket-employees/update-new", payload, headers);
      if (response?.data?.status === false || response?.data?.error === true ||
          Number(response?.data?.statusCode) >= 400) {
        throw new Error(response?.data?.message || "Gagal menyimpan Jumlah Keluhan.");
      }

      if (cacheVersionRef.current === cacheVersion) {
        updateCachedRow(semesterId, rowIndex, (item) => ({
          ...item,
          ysb_sub_element_value: Number(value),
          __savedKeluhan: String(Number(value)),
        }));
      }
      ToastSuccess.fire({ icon: "success", title: "Jumlah Keluhan berhasil disimpan" });

      // Ambil ulang semester yang diubah saja supaya KKCA (hasil hitung backend)
      // ikut diperbarui. Berpindah tab tetap menggunakan cache, tanpa request baru.
      if (cacheVersionRef.current === cacheVersion) {
        const refreshId = (refreshVersionRef.current[semesterId] || 0) + 1;
        refreshVersionRef.current[semesterId] = refreshId;
        try {
          const listResponse = await APILA.post("/api/angket-employees/index-new", {
            ysb_academic_year_id: payload.ysb_academic_year_id,
            ysb_semester_id: payload.ysb_semester_id,
            ysb_branch_id: payload.ysb_branch_id,
            ysb_school_id: payload.ysb_school_id,
          }, headers);
          if (cacheVersionRef.current === cacheVersion &&
              refreshVersionRef.current[semesterId] === refreshId) {
            const list = Array.isArray(listResponse?.data?.data) ? listResponse.data.data : [];
            const current = semesterCache.current[semesterId] || [];
            const currentById = new Map(current.map((item) => [String(item.ysb_config_anomaly_id), item]));
            const refreshed = list.filter((item) =>
              String(item?.ysb_academic_year_id) === String(payload.ysb_academic_year_id) &&
              String(item?.ysb_branch_id) === String(payload.ysb_branch_id) &&
              String(item?.ysb_semester_id) === String(payload.ysb_semester_id) &&
              String(item?.ysb_school_id) === String(payload.ysb_school_id)
            ).map((item, index) => {
              const previous = currentById.get(String(item.ysb_config_anomaly_id));
              const freshSaved = item.ysb_sub_element_value == null ? "" : String(item.ysb_sub_element_value);
              const hasDraft = previous && String(previous.ysb_sub_element_value ?? "") !==
                String(previous.__savedKeluhan ?? "");
              return {
                ...item,
                __rowIndex: index,
                __savedKeluhan: freshSaved,
                // Jangan timpa input yang masih diketik sebelum tersimpan.
                ysb_sub_element_value: hasDraft ? previous.ysb_sub_element_value : item.ysb_sub_element_value,
              };
            });
            semesterCache.current = { ...semesterCache.current, [semesterId]: refreshed };
            if (activeSemesterRef.current === semesterId) setRows(refreshed);
          }
        } catch (refreshError) {
          // PUT sudah berhasil: nilai lokal tetap tersimpan meski refresh KKCA gagal.
          console.error("Gagal memperbarui data KKCA:", refreshError);
        }
      }
    } catch (error) {
      if (cacheVersionRef.current === cacheVersion) rollback();
      ToastError.fire({
        icon: "error",
        title: error?.response?.data?.message || error?.message || "Gagal menyimpan Jumlah Keluhan.",
      });
    } finally {
      pendingSavesRef.current.delete(savingKey);
      if (mountedRef.current) {
        setSavingRows((old) => {
          const next = { ...old };
          delete next[savingKey];
          return next;
        });
      }
    }
  };

  const filteredData = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return keyword
      ? rows.filter((item) => text(item?.description_anomaly).toLowerCase().includes(keyword))
      : rows;
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  useEffect(() => {
    setTablePage((old) => Math.min(old, totalPages));
  }, [totalPages]);
  const paginatedData = useMemo(() => {
    const from = (tablePage - 1) * rowsPerPage;
    return filteredData.slice(from, from + rowsPerPage);
  }, [filteredData, tablePage, rowsPerPage]);

  return (
    <div className="body sims-page">
      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon"><FontAwesomeIcon icon={faClipboard} /></span>
            <h4 className="page-title-text">Penilaian Karyawan</h4>
          </div>
          <div className="ml-auto menu-navigation-wrapper">
            <Link to="/angket-employees/config-anomalys" style={{ textDecoration: "none", color: "#fff" }}>
              <button type="button" className="btn btn-create sims-header-button">
                <FontAwesomeIcon icon={faList} />
                <span>List Perilaku</span>
              </button>
            </Link>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {loading ? (
          <div className="sims-filter-card sims-filter-card-two-shimmer">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="shimmer sims-filter-skeleton" />
            ))}
          </div>
        ) : (
          <div className="sims-filter-card sims-filter-card-two">
            <div className="sims-filter-field">
              <label className="sims-filter-label">Tahun Ajaran</label>
              <select className="sims-filter-select" value={academicYear} onChange={changeYear}>
                <option value="" disabled>Pilih Tahun Ajaran</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}{semesters.some((item) => item.name_year === year && Number(item.status) === 1) ? " (Aktif)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Cabang</label>
              <select className="sims-filter-select" name="ysb_branch_id" value={filters.ysb_branch_id} onChange={changeFilter}>
                <option value="" disabled>Pilih Cabang</option>
                {branches.map((item) => (
                  <option key={item?.branch_code} value={item?.branch_code}>{item?.branch_name}</option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Sekolah</label>
              <select
                className="sims-filter-select"
                name="ysb_school_id"
                value={filters.ysb_school_id}
                onChange={changeFilter}
                disabled={loadingSchool || !filters.ysb_branch_id}
              >
                <option value="" disabled>{loadingSchool ? "Memuat data sekolah..." : "Pilih Sekolah"}</option>
                {schools.map((item) => (
                  <option key={item?.school_code} value={item?.school_code}>{item?.school_name}</option>
                ))}
              </select>
            </div>
            <div className="sims-filter-action">
              <button
                type="button"
                className="sims-filter-button"
                disabled={!isFilterComplete || isFetching || (hasSearched && !isFilterDirty)}
                onClick={fetchData}
              >
                Tampilkan Data
              </button>
            </div>
          </div>
        )}

        {isFetching && (
          <div className="sims-loading"><PulseLoader color="#3f5bea" size={12} /></div>
        )}

        {!loading && !isFetching && !hasSearched && (
          <div className="sims-empty-state">
            Silakan klik <strong>Tampilkan Data</strong> untuk menampilkan data sesuai filter.
          </div>
        )}

        {hasSearched && !isFetching && (
          <>
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="sims-search-icon" />
              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari perilaku"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setTablePage(1); }}
              />
            </div>

            <div className="sims-table-tab-card">
              <div className="sims-table-tab-head">
                <div className="sims-table-tab-title">
                  <h5>Penilaian Karyawan - Semester {selectedSemester?.semester || "-"}</h5>
                </div>
                <div className="sims-table-tab-semester-switch">
                  {semesterOptions.map((semester) => (
                    <button
                      type="button"
                      key={semester?.id}
                      className={`sims-table-tab-semester ${String(filters.ysb_semester_id) === String(semester?.id) ? "is-active" : ""}`}
                      onClick={() => switchSemester(semester)}
                      disabled={isFetching}
                    >
                      Semester {semester?.semester}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sims-table-tab-wrap">
                <table className="sims-table-tab walas-table-column-borders">
                  <thead>
                    <tr>
                      <th className="sims-col-number">No</th>
                      <th>Perilaku</th>
                      <th className="nilai-keluhan-col">Jumlah Keluhan</th>
                      <th className="nilai-kkca-col">KKCA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="sims-empty-state">
                          <div className="sims-empty-state-content">Data perilaku tidak ditemukan</div>
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item, index) => (
                        <tr key={`${filters.ysb_semester_id}-${item?.ysb_config_anomaly_id || item.__rowIndex}-${item.__rowIndex}`}>
                          <td className="sims-col-number">
                            <span className="sims-number-badge">{(tablePage - 1) * rowsPerPage + index + 1}</span>
                          </td>
                          <td className="nilai-perilaku-cell">{item?.description_anomaly || "-"}</td>
                          <td className="nilai-input-cell">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              inputMode="numeric"
                              className="nilai-keluhan-input"
                              aria-label={`Jumlah keluhan: ${item?.description_anomaly || "perilaku"}`}
                              title="Ubah angka, lalu tekan Enter atau klik di luar kolom untuk menyimpan"
                              value={item?.ysb_sub_element_value ?? ""}
                              disabled={!!savingRows[`${filters.ysb_semester_id}:${item.ysb_school_id}:${item.ysb_config_anomaly_id}`]}
                              onChange={(event) => changeKeluhan(item.__rowIndex, event.target.value)}
                              onBlur={(event) => saveKeluhan(item.__rowIndex, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") event.currentTarget.blur();
                                if (["e", "E", "+", "-", "."].includes(event.key)) event.preventDefault();
                              }}
                            />
                            {savingRows[`${filters.ysb_semester_id}:${item.ysb_school_id}:${item.ysb_config_anomaly_id}`] && (
                              <span className="nilai-saving-text">Menyimpan...</span>
                            )}
                          </td>
                          <td className="nilai-kkca-cell">{item?.nilai_kkca ?? "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                totalRows={filteredData.length}
                tablePage={tablePage}
                rowsPerPage={rowsPerPage}
                onPageChange={setTablePage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(Number(value));
                  setTablePage(1);
                }}
              />
              <p className="nilai-save-hint">
                Ubah Jumlah Keluhan, lalu tekan Enter atau klik di luar kolom untuk menyimpan.
              </p>
            </div>
          </>
        )}
      </div>

      <style>{`
        .walas-table-column-borders > thead > tr > th,
        .walas-table-column-borders > tbody > tr > td {
          border-right: 1px solid #d7e2f7 !important;
        }
        .walas-table-column-borders > thead > tr > th:last-child,
        .walas-table-column-borders > tbody > tr > td:last-child {
          border-right: 0 !important;
        }
        .nilai-perilaku-cell {
          font-size: 13px;
          vertical-align: middle !important;
          min-width: 240px;
          overflow-wrap: anywhere;
        }
        .nilai-keluhan-col, .nilai-kkca-col {
          text-align: center !important;
          width: 160px;
        }
        .nilai-input-cell, .nilai-kkca-cell {
          text-align: center !important;
          vertical-align: middle !important;
          font-size: 13px;
        }
        .nilai-keluhan-input {
          display: block;
          width: 100px;
          max-width: 100%;
          height: 35px;
          margin: 0 auto;
          padding: 6px 9px;
          border: 1px solid #d7e2f7;
          border-radius: 8px;
          outline: none;
          background: #fff;
          color: #1e293b;
          text-align: center;
          font-size: 13px;
          transition: border-color .15s, box-shadow .15s;
        }
        .nilai-keluhan-input:focus {
          border-color: #3f5bea;
          box-shadow: 0 0 0 3px rgba(63, 91, 234, .12);
        }
        .nilai-save-hint {
          margin: 4px 16px 16px;
          font-size: 12px;
          color: #64748b;
        }
        .nilai-saving-text {
          display: block;
          font-size: 11px;
          color: #64748b;
          margin-top: 4px;
        }
        .nilai-keluhan-input:disabled {
          background: #f1f5f9;
          opacity: .7;
          cursor: wait;
        }
        @media (max-width: 768px) {
          .nilai-perilaku-cell { min-width: 200px; }
          .nilai-keluhan-col, .nilai-kkca-col { min-width: 125px; }
        }
      `}</style>
    </div>
  );
}
